import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Sparkles,
  FileText,
  ChevronRight,
  TrendingUp,
  Star,
  Loader2,
  Lightbulb
} from 'lucide-react';

const PersonalizedRecommendations = () => {
  const { api } = useAuth();
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const response = await api.get('/analytics/personalized-recommendations');
      setRecommendations(response.data);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Compraventa': 'bg-blue-500/20 text-blue-400',
      'Arrendamiento': 'bg-cyan-500/20 text-cyan-400',
      'Laboral': 'bg-emerald-500/20 text-emerald-400',
      'Servicios': 'bg-purple-500/20 text-purple-400',
      'Societario': 'bg-amber-500/20 text-amber-400',
      'Familia': 'bg-pink-500/20 text-pink-400'
    };
    return colors[category] || 'bg-slate-500/20 text-slate-400';
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 mb-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
          <span className="ml-2 text-slate-400 text-sm">Analizando tu historial...</span>
        </div>
      </div>
    );
  }

  if (!recommendations || recommendations.recommendations.length === 0) {
    return null;
  }

  const { recommendations: recs, user_pattern } = recommendations;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden mb-6">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-border bg-gradient-to-r from-purple-500/10 to-pink-500/10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold font-['Outfit'] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Recomendado para Ti
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {user_pattern.message}
              {user_pattern.favorite_types.length > 0 && (
                <> • Más usados: {user_pattern.favorite_types.map(t => t.type).join(', ')}</>
              )}
            </p>
          </div>
          <Link 
            to="/recommend"
            className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
          >
            Ver más
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Recommendations Grid */}
      <div className="p-4 md:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {recs.slice(0, 6).map((rec, index) => (
            <Link
              key={index}
              to={`/discovery?template=${encodeURIComponent(rec.template)}`}
              data-testid={`recommendation-${index}`}
              className="group bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-purple-500/30 rounded-xl p-4 transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-2">
                <div className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(rec.category)}`}>
                  {rec.category}
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Star className="w-3 h-3 fill-current text-amber-400" />
                  {rec.relevance}%
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-purple-500/20 transition-colors">
                  <FileText className="w-4 h-4 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-white text-sm leading-tight group-hover:text-purple-300 transition-colors">
                    {rec.template}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                    {rec.reason}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Tip */}
        <div className="mt-4 p-3 bg-slate-800/30 rounded-lg flex items-start gap-3">
          <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <p className="text-sm text-slate-300">
              <span className="font-medium">Tip:</span> Cuantos más contratos crees, mejores serán las recomendaciones personalizadas.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalizedRecommendations;
